import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { type ReactNode } from 'react';

import { cn } from '@/utils/cn';

import { CopyField } from '../data-display/CopyField';
import { Button } from '../ui/Button';
import { IconWell } from '../ui/IconWell';

export interface ErrorStateProps {
  /** Already localized. */
  title: string;
  /** Already localized. */
  body: string;
  icon?: ReactNode;
  onRetry?: (() => void) | undefined;
  /** Already localized. */
  retryLabel?: string | undefined;
  /** Already localized. */
  retryLoadingLabel?: string | undefined;
  isRetrying?: boolean;
  requestId?: string | undefined;
  publicId?: string | undefined;
  /** Already localized. */
  requestIdLabel?: string | undefined;
  /** Already localized. */
  publicIdLabel?: string | undefined;
  copyLabel: string;
  copiedLabel: string;
  manualCopyLabel: string;
  className?: string;
}

/**
 * Retry is omitted (not disabled) when the error is not retryable. A disabled retry button on a
 * 401 just invites tapping; a missing button tells the truth.
 *
 * The only identifiers shown are `publicId` and `requestId`. Internal ids stay off the screen.
 */
export function ErrorState({
  title,
  body,
  icon,
  onRetry,
  retryLabel,
  retryLoadingLabel,
  isRetrying = false,
  requestId,
  publicId,
  requestIdLabel,
  publicIdLabel,
  copyLabel,
  copiedLabel,
  manualCopyLabel,
  className,
}: ErrorStateProps) {
  return (
    <div className={cn('flex flex-col items-center gap-3 py-4 text-center', className)}>
      <IconWell tone="danger">{icon ?? <ExclamationTriangleIcon />}</IconWell>
      <h1 className="text-text text-lg font-semibold tracking-tight" tabIndex={-1}>
        {title}
      </h1>
      <p className="text-text-muted max-w-prose text-sm leading-relaxed">{body}</p>
      {onRetry !== undefined && retryLabel !== undefined && (
        <Button
          variant="secondary"
          onClick={onRetry}
          isLoading={isRetrying}
          loadingLabel={retryLoadingLabel}
        >
          {retryLabel}
        </Button>
      )}
      {(requestId !== undefined || publicId !== undefined) && (
        <div className="flex w-full max-w-sm flex-col gap-2 text-start">
          {publicId !== undefined && publicIdLabel !== undefined && (
            <CopyField
              label={publicIdLabel}
              value={publicId}
              copyLabel={copyLabel}
              copiedLabel={copiedLabel}
              manualCopyLabel={manualCopyLabel}
            />
          )}
          {requestId !== undefined && requestIdLabel !== undefined && (
            <CopyField
              label={requestIdLabel}
              value={requestId}
              copyLabel={copyLabel}
              copiedLabel={copiedLabel}
              manualCopyLabel={manualCopyLabel}
            />
          )}
        </div>
      )}
    </div>
  );
}
